import React, { forwardRef } from 'react';
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant
   * @default "outline"
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  /**
   * Button size
   * @default "md"
   */
  size?: 'sm' | 'md';
  /**
   * Full width button
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Optional icon before button text
   */
  iconLeft?: React.ReactNode;
  /**
   * Optional icon after button text
   */
  iconRight?: React.ReactNode;
  /**
   * Optional data-id for testing
   */
  'data-id'?: string;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'outline',
  size = 'md',
  fullWidth = false,
  iconLeft,
  iconRight,
  className = '',
  disabled,
  'data-id': dataId,
  ...props
}, ref) => {
  // Base classes - Ocean Design System
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';
  // Size classes - Ocean standard: 40px default, 32px small
  const sizeClasses = {
    sm: 'h-8 px-3 text-[13px] gap-2',
    md: 'h-10 px-5 text-[14px] gap-2'
  };
  // Variant classes with Ocean Design System colors
  const variantClasses = {
    primary: 'bg-[#0EA5E9] text-white hover:bg-[#0284C7] active:bg-[#0369A1] focus:shadow-[0_0_0_3px_rgba(14,165,233,0.2)] hover:scale-[1.02] active:scale-100',
    secondary: 'bg-white text-[#0F172A] border border-[#CBD5E1] hover:bg-[#F1F5F9] hover:border-[#94A3B8] active:bg-[#E2E8F0] focus:border-[#0EA5E9] focus:shadow-[0_0_0_3px_rgba(14,165,233,0.2)]',
    ghost: 'bg-transparent text-[#0EA5E9] hover:bg-[rgba(14,165,233,0.1)] active:bg-[rgba(14,165,233,0.2)]',
    danger: 'bg-[#EF4444] text-white hover:bg-[#DC2626] active:bg-[#B91C1C] focus:shadow-[0_0_0_3px_rgba(239,68,68,0.2)]',
    outline: 'bg-white text-[#0EA5E9] border border-[#0EA5E9] hover:bg-[rgba(14,165,233,0.1)] active:bg-[rgba(14,165,233,0.2)] focus:border-[#0EA5E9] focus:shadow-[0_0_0_3px_rgba(14,165,233,0.2)]'
  };
  // Width classes
  const widthClasses = fullWidth ? 'w-full' : 'w-auto';
  // Icon sizing based on button size
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  // Combine all classes
  const buttonClasses = `
      ${baseClasses}
      ${sizeClasses[size]}
      ${variantClasses[variant]}
      ${widthClasses}
      ${className}
    `.trim();
  return <button ref={ref} className={buttonClasses} disabled={disabled} {...props} data-id="element-3267">
        {iconLeft && <span className={`flex-shrink-0 ${iconSize}`} data-id="element-3268">{iconLeft}</span>}
        {children && <span className="whitespace-nowrap" data-id="element-3269">{children}</span>}
        {iconRight && <span className={`flex-shrink-0 ${iconSize}`} data-id="element-3270">{iconRight}</span>}
      </button>;
});
Button.displayName = 'Button';